import { CdkDragEnd, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Employee } from '@model/employee';
import { Reservation, Table } from '@model/reservation';
import { Section, TableImageType, TableRotation, TablesMode, WorkingDate } from '@model/tables-plan';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { State } from '@store/index';
import * as EmployeeSelectors from '@store/selectors/employee.selectors';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Toast } from 'bootstrap';
import { Observable, Subject, takeUntil } from 'rxjs';
import { rect10_Dimension, rect2_Dimension, rect4_Dimension, rect6_Dimension, rect8_Dimension, round10_Dimension, round2_Dimension, round4_Dimension, round6_Dimension, round8_Dimension, square2_Dimension, square4_Dimension, tableImageSizeFactor } from 'src/app/constants';
import { Common } from 'src/app/lib/common-methods';
import { ModalDialogComponent } from 'src/app/modal-dialog/modal-dialog.component';
import { TablesPlanService } from '../tables-plan.service';
declare const bootstrap: any;

@Component({
  selector: 'app-toolbox',
  templateUrl: './toolbox.component.html',
  styleUrl: './toolbox.component.scss',
})
export class ToolboxComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private store: Store<State>,
    private tablesPlanService: TablesPlanService,
    private translateService: TranslateService,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  editingTable$: Observable<{ table: Table, isNew: boolean } | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTable));
  editingTable: { table: Table, isNew: boolean } | null = null;
  editingTableCopy$: Observable<Table | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTableCopy));
  editingTableCopy: Table | null = null;
  workingDate$: Observable<WorkingDate | null | undefined> = this.store.pipe(select(TablesPlanSelectors.selectWorkingDate));
  workingDate: WorkingDate | null | undefined = null;
  selectedSection$: Observable<Section | null | undefined> = this.store.pipe(select(TablesPlanSelectors.selectSelectedSection));
  selectedSection: Section | null | undefined = null;
  sectionTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSectionTables));
  sectionTables: Table[] = [];
  zoom$: Observable<number> = this.store.pipe(select(TablesPlanSelectors.selectZoom));
  zoom: number = 1;
  canvasRect$: Observable<DOMRect | null> = this.store.pipe(select(TablesPlanSelectors.selectCanvasRect));
  canvasRect: DOMRect | null = null;
  loggedInemployee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  loggedInemployee: Employee | null = null;
  reservations$: Observable<Reservation[]> = this.store.pipe(select(ReservationListSelectors.selectAllReservations));
  reservations: Reservation[] = [];
  tablesMode$: Observable<TablesMode> = this.store.pipe(select(TablesPlanSelectors.selectTablesMode));
  tablesMode: TablesMode = 'Tables';
  sectionJoinedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSectionJoinedTables));
  sectionJoinedTables: Table[] = [];

  @ViewChild('deleteTableConfirmationModal') deleteTableConfirmationModal!: ModalDialogComponent;
  @ViewChild('deleteJoinedTableConfirmationModal') deleteJoinedTableConfirmationModal!: ModalDialogComponent;
  @ViewChild('deleteTableWithAssignedReservationConfirmationModal') deleteTableWithAssignedReservationConfirmationModal!: ModalDialogComponent;
  @ViewChild('deleteJoinTableOnRenameTableConfirmationModal') deleteJoinTableOnRenameTableConfirmationModal!: ModalDialogComponent;
  @ViewChild('toast') toastElement!: ElementRef;

  @HostListener('document:touchstart', ['$event'])
  handleTouchStart(event: TouchEvent) {

    const tableTypeElement = (event.target as HTMLElement).closest(".table-type");

    if (tableTypeElement == null) {
      return;
    }

    const imageType = tableTypeElement.getAttribute('data-image-type');

    this.tableMouseDown(event, imageType as TableImageType);
  }

  @HostListener('document:touchend', ['$event'])
  handleTouchEnd(event: TouchEvent) {
    this.tableMouseUp(event);
  }

  toast: Toast | null = null;
  validationMessage: string | null = '';
  oldTableName: string = '';
  dragHandleOffset: { x: number, y: number } = { x: 0, y: 0 };
  joinedTableToDelete: Table | null = null;
  dndEnabled: boolean = false;
  draggedTableType: TableImageType | null = null

  // on change update the same variable in table.component.scss

  // on heights change update heights in table.component.scss
  tableCategories: { covers: number, tableTypes: { imageType: TableImageType, instances: number[], height: number }[] }[] = [
    {
      covers: 2, tableTypes: [
        { imageType: 'round2', instances: [1], height: round2_Dimension },
        { imageType: 'square2', instances: [1], height: square2_Dimension },
        { imageType: 'rect2', instances: [1], height: rect2_Dimension },
      ]
    },
    {
      covers: 4, tableTypes: [
        { imageType: 'round4', instances: [1], height: round4_Dimension },
        { imageType: 'square4', instances: [1], height: square4_Dimension },
        { imageType: 'rect4', instances: [1], height: rect4_Dimension },
      ]
    },
    {
      covers: 6, tableTypes: [
        { imageType: 'round6', instances: [1], height: round6_Dimension },
        { imageType: 'rect6', instances: [1], height: rect6_Dimension },
      ]
    },
    {
      covers: 8, tableTypes: [
        { imageType: 'round8', instances: [1], height: round8_Dimension },
        { imageType: 'rect8', instances: [1], height: rect8_Dimension },
      ]
    },
    {
      covers: 10, tableTypes: [
        { imageType: 'round10', instances: [1], height: round10_Dimension },
        { imageType: 'rect10', instances: [1], height: rect10_Dimension },
      ]
    },
  ]

  ngOnInit(): void {

    this.editingTable$.pipe(takeUntil(this.destroy$)).subscribe((editingTable) => {
      this.editingTable = editingTable;
    });

    this.editingTableCopy$.pipe(takeUntil(this.destroy$)).subscribe((editingTableCopy) => {
      this.editingTableCopy = editingTableCopy;
    });

    this.workingDate$.pipe(takeUntil(this.destroy$)).subscribe((workingDate: WorkingDate | null | undefined) => {
      this.workingDate = workingDate;
    });

    this.selectedSection$.pipe(takeUntil(this.destroy$)).subscribe((selectedSection: Section | null | undefined) => {
      this.selectedSection = selectedSection;
    });

    this.canvasRect$.pipe(takeUntil(this.destroy$)).subscribe((canvasRect: DOMRect | null) => {
      this.canvasRect = canvasRect;
    });

    this.sectionTables$.pipe(takeUntil(this.destroy$)).subscribe((sectionTables: Table[]) => {
      this.sectionTables = sectionTables;
    });

    this.zoom$.pipe(takeUntil(this.destroy$)).subscribe((zoom: number) => {
      this.zoom = zoom;
    });

    this.loggedInemployee$.pipe(takeUntil(this.destroy$)).subscribe((loggedInemployee) => {
      this.loggedInemployee = loggedInemployee;
    });

    this.reservations$.pipe(takeUntil(this.destroy$)).subscribe((reservations) => {
      this.reservations = reservations;
    });

    this.tablesMode$.pipe(takeUntil(this.destroy$)).subscribe((tablesMode) => {
      this.tablesMode = tablesMode;
    });

    this.sectionJoinedTables$.pipe(takeUntil(this.destroy$)).subscribe((sectionJoinedTables) => {
      this.sectionJoinedTables = sectionJoinedTables;
    });
  }

  ngAfterViewInit(): void {
    this.toast = bootstrap.Toast.getOrCreateInstance(this.toastElement.nativeElement);
  }

  dragStarted(event: CdkDragStart) {
    const { tableCategory, tableType } = event.source.data;

    // add an instance as a clone for the dragged table
    tableType.instances.push(1);
    event.source.element.nativeElement.classList.add('dragging');

    const dragElementRect = event.source.element.nativeElement.getBoundingClientRect();

    const mouseEvent = event.event as MouseEvent;

    const tableHeight = tableType.height * tableImageSizeFactor;
    const toolboxTableToCanvasTableSizeFactor = tableHeight / dragElementRect.height;

    let x, y: number;
    if (mouseEvent.clientX) {
      x = ((event.event as MouseEvent).x - dragElementRect.x) * toolboxTableToCanvasTableSizeFactor;
      y = ((event.event as MouseEvent).y - dragElementRect.y) * toolboxTableToCanvasTableSizeFactor;
    } else {
      const touchEvent = event.event as TouchEvent;
      const touch = touchEvent.touches[0];

      x = (touch.clientX - dragElementRect.x) * toolboxTableToCanvasTableSizeFactor;
      y = (touch.clientY - dragElementRect.y) * toolboxTableToCanvasTableSizeFactor;
    }

    this.dragHandleOffset = { x, y };
  }

  dragMoved(event: CdkDragMove) {

  }

  dragEnded(event: CdkDragEnd) {
    const { tableCategory, tableType } = event.source.data;
    const { dropPoint } = event;
    const { x, y } = dropPoint;

    const target: HTMLElement = event.event.target as HTMLElement;
    const canvasElement = document.getElementById('canvas');

    // remove the cloned instance
    tableType.instances.pop();

    event.source.element.nativeElement.classList.remove('dragging');

    // reset draggable, remove transform: translate
    event.source._dragRef.reset();

    if (x < 200) {
      return;
    }

    const scrollLeft = canvasElement?.scrollLeft ?? 0;
    const scrollTop = canvasElement?.scrollTop ?? 0;

    const offset = this.tablesPlanService.getTableCoordinatesOffset(tableType.imageType);

    this.setEditingTable(
      tableType.imageType,
      ((x - (this.canvasRect?.x ?? 0) + scrollLeft) / this.zoom - this.dragHandleOffset.x) + offset,
      ((y - (this.canvasRect?.y ?? 0) + scrollTop) / this.zoom - this.dragHandleOffset.y) + offset,
      tableCategory.covers
    );

    this.dndEnabled = false;
  }

  tableMouseDownTimeout: NodeJS.Timeout | undefined;
  tableMouseDown(event: MouseEvent | TouchEvent, imageType: TableImageType) {
    this.draggedTableType = imageType;

    this.tableMouseDownTimeout = setTimeout(() => {
      this.dndEnabled = true;
    }, 100);
  }

  tableMouseUp(event: MouseEvent | TouchEvent) {
    if (this.tableMouseDownTimeout != null) {
      clearTimeout(this.tableMouseDownTimeout);
    }
    this.dndEnabled = false;
  }

  setEditingTable(tableType: TableImageType, x: number, y: number, covers: number) {
    let maxTableOrder = Math.max(...this.sectionTables.map(t => t.tableOrder));
    if (maxTableOrder <= 0) {
      maxTableOrder = 0;
    }

    const table = {
      tableName: '',
      tableOrder: maxTableOrder + 1,
      imgType: tableType,
      minCapacity: Math.floor(covers / 2),
      maxCapacity: covers,
      rotation: 360,
      workingDateId: this.workingDate?.workingDateId,
      sectionId: this.selectedSection?.sectionId,
      tableId: Common.generateUuidBase64Encoded(),
      x: this.tablesPlanService.pixelsToCoordinate(x),
      y: this.tablesPlanService.pixelsToCoordinate(y),
    };

    this.store.dispatch({ type: TablesPlanActions.setEditingTable.type, table, isNew: true });
  }

  setEditingTableName(name: string) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTableName.type, name });
  }

  setEditingTableMinCapacity(min: number) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTableMinCapacity.type, min });
  }

  setEditingTableMaxCapacity(max: number) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTableMaxCapacity.type, max });
  }

  setEditingTableRotation(rotation: TableRotation) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTableRotation.type, rotation });
  }

  setEditingTableOrder(order: number) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTableOrder.type, order });
  }

  setJoinedTableMinCapacity(joinedTable: Table, min: number) {
    const updatedTable = JSON.parse(JSON.stringify(joinedTable));
    updatedTable.minCapacity = min;
    this.store.dispatch({ type: TablesPlanActions.updateJoinedTable.type, joinedTable: updatedTable, modifiedBy: this.loggedInemployee?.surname });
  }

  setJoinedTableMaxCapacity(joinedTable: Table, max: number) {
    const updatedTable = JSON.parse(JSON.stringify(joinedTable));
    updatedTable.maxCapacity = max;
    this.store.dispatch({ type: TablesPlanActions.updateJoinedTable.type, joinedTable: updatedTable, modifiedBy: this.loggedInemployee?.surname });
  }

  addJoinedTable() {
    this.store.dispatch({ type: TablesPlanActions.setAddJoinedTablesMode.type, addJoinedTablesMode: true });
  }

  saveTableProperties() {

    const validationError = this.tablesPlanService.isTableValid(this.editingTable?.table as Table);

    if (!Common.isNullOrEmpty(validationError)) {
      // show validation message
      this.validationMessage = validationError;
      this.toast?.show();
      return;
    }

    if (!this.editingTable?.isNew && this.editingTable?.table.tableName !== this.editingTableCopy?.tableName) {
      this.deleteJoinTableOnRenameTableConfirmationModal.show();
      return;
    }

    this.saveEditingTable(true);
  }

  saveEditingTable(confirmed: boolean) {
    if (!confirmed) {
      return;
    }
    this.store.dispatch({
      type: TablesPlanActions.saveEditingTable.type,
      modifiedBy: this.loggedInemployee?.surname
    });
  }

  cancelEditTable() {
    this.store.dispatch({
      type: TablesPlanActions.cancelEditTable.type,
    });
  }

  deleteTableWithTableAssignedCheck(confirmed: boolean) {
    if (!confirmed) {
      return;
    }

    const tableHasAssignedReservation = this.reservations.some(r => Common.tableReservationsFilter(this.editingTable?.table as Table, r));

    if (tableHasAssignedReservation) {
      this.deleteTableWithAssignedReservationConfirmationModal.show();
      return;
    }

    this.deleteTable(true);
  }

  deleteTable(confirmed: boolean) {
    if (!confirmed) {
      return;
    }

    this.store.dispatch({
      type: TablesPlanActions.deleteEditingTable.type,
      modifiedBy: this.loggedInemployee?.surname
    });
  }

  showDeleteJoinedTableConfirmation(joinedTable: Table) {
    this.joinedTableToDelete = joinedTable;
    this.deleteJoinedTableConfirmationModal.show();
  }

  deleteJoinedTable(confirmed: boolean) {
    if (!confirmed) {
      return;
    }

    this.store.dispatch({
      type: TablesPlanActions.deleteJoinedTable.type,
      joinedTable: this.joinedTableToDelete
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
